import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, X } from "lucide-react";

interface NewsFiltersProps {
  categories: string[];
  selectedCategory: string;
  onCategoryChange: (category: string) => void;
  query: string;
  onQueryChange: (query: string) => void;
  resultCount: number;
  totalCount: number;
  onClear: () => void;
}

const ALL = "All";

const NewsFilters = ({
  categories,
  selectedCategory,
  onCategoryChange,
  query,
  onQueryChange,
  resultCount,
  totalCount,
  onClear,
}: NewsFiltersProps) => {
  const isFiltered = selectedCategory !== ALL || query.trim().length > 0;

  return (
    <div className="mb-10 space-y-4">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        {/* Category chips */}
        <div className="flex flex-wrap gap-2" role="group" aria-label="Filter by category">
          {[ALL, ...categories].map((cat) => {
            const active = cat === selectedCategory;
            return (
              <Button
                key={cat}
                size="sm"
                variant={active ? "primary" : "secondary"}
                onClick={() => onCategoryChange(cat)}
                aria-pressed={active}
              >
                {cat}
              </Button>
            );
          })}
        </div>

        {/* Search */}
        <div className="relative w-full lg:w-72 shrink-0">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
          <Input
            type="search"
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            placeholder="Search articles…"
            className="pl-9 pr-9"
            aria-label="Search articles"
          />
          {query && (
            <button
              type="button"
              onClick={() => onQueryChange("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              aria-label="Clear search"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between text-sm text-muted-foreground">
        <span>
          Showing <span className="text-foreground font-medium">{resultCount}</span> of {totalCount} articles
        </span>
        {isFiltered && (
          <Button variant="tertiary" size="sm" onClick={onClear}>
            Clear filters
          </Button>
        )}
      </div>
    </div>
  );
};

export default NewsFilters;
